import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Circle, Users, X } from 'lucide-react';
import { formatClock, idKey, remainingSessionSpots } from '../../../utils/courseSessionPattern';

function sessionDayLabel(sessionDate, language) {
    const [year, month, day] = String(sessionDate).slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(language || 'it-IT', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
}

function toIdSet(value) {
    if (!value) return new Set();
    if (value instanceof Set) return new Set([...value].map(Number));
    if (Array.isArray(value)) return new Set(value.map(Number));
    return new Set();
}

function waitlistNeeded(row) {
    if (!row) return 0;
    if (row.free === 0) return row.candidateIds.length;
    return Math.max(0, row.candidateIds.length - row.free);
}

function isRowReady(row) {
    if (!row) return false;
    if (row.candidateIds.length === 0) return true;
    if (row.free === 0) return true;
    return row.waitlistUserIds.length === waitlistNeeded(row);
}

/**
 * Analizza posti vs utenti selezionati, escludendo già iscritti / già in lista.
 */
export function analyzeBulkEnrollmentCapacity(
    selectedSessionIds,
    selectedMemberIds,
    sessionsById,
    alreadyBySession = {}
) {
    const members = selectedMemberIds.map((id) => Number(id));
    const okSessions = [];
    const conflictSessions = [];

    selectedSessionIds.forEach((sessionId) => {
        const session = sessionsById[idKey(sessionId)];
        if (!session) return;

        const already = alreadyBySession[idKey(sessionId)] || {};
        const enrolledSet = toIdSet(already.enrolled);
        const waitlistedSet = toIdSet(already.waitlisted);

        const duplicateUserIds = members.filter((id) => enrolledSet.has(id));
        const alreadyWaitlistUserIds = members.filter(
            (id) => waitlistedSet.has(id) && !enrolledSet.has(id)
        );
        const candidateIds = members.filter(
            (id) => !enrolledSet.has(id) && !waitlistedSet.has(id)
        );

        const free = remainingSessionSpots(session);
        const base = {
            session,
            free,
            memberIds: members,
            candidateIds,
            duplicateUserIds,
            alreadyWaitlistUserIds,
        };

        if (candidateIds.length === 0) {
            okSessions.push({
                ...base,
                enrollUserIds: [],
                waitlistUserIds: [],
            });
            return;
        }

        if (!Number.isFinite(free) || free >= candidateIds.length) {
            okSessions.push({
                ...base,
                enrollUserIds: candidateIds,
                waitlistUserIds: [],
            });
            return;
        }

        conflictSessions.push({
            ...base,
            enrollUserIds: candidateIds.slice(0, free),
            waitlistUserIds: candidateIds.slice(free),
        });
    });

    return { okSessions, conflictSessions };
}

export default function BulkEnrollmentCapacityModal({
    isOpen,
    conflicts,
    okCount = 0,
    membersById,
    onCancel,
    onConfirm,
}) {
    const { t, i18n } = useTranslation();
    const [draft, setDraft] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        const nextDraft = conflicts.map((item) => ({
            sessionId: Number(item.session.id),
            free: item.free,
            memberIds: item.memberIds.map(Number),
            candidateIds: (item.candidateIds || item.memberIds).map(Number),
            duplicateIds: (item.duplicateUserIds || []).map(Number),
            alreadyWaitlistIds: (item.alreadyWaitlistUserIds || []).map(Number),
            waitlistUserIds: (item.waitlistUserIds || []).map(Number),
        }));
        setDraft(nextDraft);
        setActiveSessionId(nextDraft[0]?.sessionId ?? null);
    }, [isOpen, conflicts]);

    const readyCount = useMemo(
        () => draft.filter((row) => isRowReady(row)).length,
        [draft]
    );

    const validationError = useMemo(() => {
        for (const row of draft) {
            if (row.free === 0 || row.candidateIds.length === 0) continue;
            const needed = waitlistNeeded(row);
            if (row.waitlistUserIds.length !== needed) {
                return t('gym.bulkEnrollment.capacity.pickExact', {
                    count: needed,
                    free: row.free,
                    users: row.candidateIds.length,
                });
            }
        }
        return null;
    }, [draft, t]);

    const activeRow = draft.find((row) => row.sessionId === activeSessionId) || null;
    const activeConflict = conflicts.find(
        (item) => Number(item.session.id) === activeSessionId
    );
    const activeSession = activeConflict?.session;

    if (!isOpen) return null;

    const toggleWaitlist = (sessionId, userId) => {
        setDraft((prev) =>
            prev.map((row) => {
                if (row.sessionId !== sessionId || row.free === 0) return row;
                if (!row.candidateIds.includes(userId)) return row;
                const needed = waitlistNeeded(row);
                const has = row.waitlistUserIds.includes(userId);
                let nextWait;
                if (has) {
                    nextWait = row.waitlistUserIds.filter((id) => id !== userId);
                } else if (row.waitlistUserIds.length >= needed) {
                    nextWait = [...row.waitlistUserIds.slice(1), userId];
                } else {
                    nextWait = [...row.waitlistUserIds, userId];
                }
                return { ...row, waitlistUserIds: nextWait };
            })
        );
    };

    const handleConfirm = () => {
        if (validationError) return;
        const plan = draft.map((row) => {
            const waitSet = new Set(row.waitlistUserIds.map(Number));
            const candidateSet = new Set(row.candidateIds.map(Number));
            return {
                sessionId: row.sessionId,
                enrollUserIds:
                    row.free === 0
                        ? []
                        : row.candidateIds.filter((id) => !waitSet.has(Number(id))),
                waitlistUserIds:
                    row.free === 0
                        ? row.candidateIds
                        : row.waitlistUserIds.filter((id) => candidateSet.has(Number(id))),
                duplicateUserIds: row.duplicateIds,
                alreadyWaitlistUserIds: row.alreadyWaitlistIds,
            };
        });
        onConfirm(plan);
    };

    const needed = activeRow ? waitlistNeeded(activeRow) : 0;
    const capacityLabel = (row) => {
        if (row.free === 0) return t('gym.bulkEnrollment.capacity.fullLabel');
        if (row.duplicateIds.length > 0) {
            return t('gym.bulkEnrollment.capacity.partialLabelWithDuplicates', {
                free: row.free,
                users: row.candidateIds.length,
                duplicates: row.duplicateIds.length,
            });
        }
        return t('gym.bulkEnrollment.capacity.partialLabel', {
            free: row.free,
            users: row.candidateIds.length,
        });
    };

    const memberStatus = (row, userId) => {
        if (row.duplicateIds.includes(userId)) return 'duplicate';
        if (row.alreadyWaitlistIds.includes(userId)) return 'alreadyWaitlist';
        if (row.free === 0 || row.waitlistUserIds.includes(userId)) return 'waitlist';
        return 'enroll';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="flex h-[min(90vh,720px)] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-900">
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-white/10">
                    <div className="flex items-start gap-3">
                        <span className="mt-0.5 rounded-lg bg-amber-100 p-2 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                            <AlertTriangle size={20} aria-hidden />
                        </span>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t('gym.bulkEnrollment.capacity.title')}
                            </h3>
                            <p className="mt-1 text-sm text-gray-600 dark:text-white/70">
                                {t('gym.bulkEnrollment.capacity.subtitle')}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10"
                        aria-label={t('gym.bulkEnrollment.capacity.cancel')}
                    >
                        <X size={20} />
                    </button>
                </div>

                {okCount > 0 && (
                    <p className="shrink-0 border-b border-emerald-200 bg-emerald-50 px-5 py-2.5 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/30 dark:text-emerald-200">
                        {t('gym.bulkEnrollment.capacity.okSessionsNote', { count: okCount })}
                    </p>
                )}

                <div className="flex min-h-0 flex-1 flex-col md:flex-row">
                    <aside className="flex max-h-[40%] w-full shrink-0 flex-col border-b border-gray-200 dark:border-white/10 md:max-h-none md:w-[280px] md:border-b-0 md:border-r">
                        <div className="flex shrink-0 items-center justify-between px-4 py-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-white/55">
                                {t('gym.bulkEnrollment.capacity.sessionsList', {
                                    count: draft.length,
                                })}
                            </p>
                            <span className="text-xs font-medium text-gray-500 dark:text-white/55">
                                {readyCount}/{draft.length}
                            </span>
                        </div>
                        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 pb-3">
                            {draft.map((row) => {
                                const conflict = conflicts.find(
                                    (item) => Number(item.session.id) === row.sessionId
                                );
                                const session = conflict?.session;
                                if (!session) return null;
                                const selected = row.sessionId === activeSessionId;
                                const ready = isRowReady(row);
                                return (
                                    <li key={row.sessionId}>
                                        <button
                                            type="button"
                                            onClick={() => setActiveSessionId(row.sessionId)}
                                            className={`flex w-full items-start gap-2 rounded-lg px-3 py-2.5 text-left transition-colors ${
                                                selected
                                                    ? 'bg-indigo-50 ring-1 ring-indigo-200 dark:bg-indigo-950/40 dark:ring-indigo-500/40'
                                                    : 'hover:bg-gray-50 dark:hover:bg-white/5'
                                            }`}
                                        >
                                            <span className="mt-0.5 shrink-0">
                                                {ready ? (
                                                    <CheckCircle2
                                                        size={16}
                                                        className="text-emerald-600 dark:text-emerald-400"
                                                        aria-hidden
                                                    />
                                                ) : (
                                                    <Circle
                                                        size={16}
                                                        className="text-amber-500 dark:text-amber-400"
                                                        aria-hidden
                                                    />
                                                )}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                                                    {session.course_title || session.title}
                                                </span>
                                                <span className="block truncate text-xs text-gray-500 dark:text-white/60">
                                                    {sessionDayLabel(session.session_date, i18n.language)}{' '}
                                                    {formatClock(session.start_time)}
                                                </span>
                                                <span
                                                    className={`mt-0.5 block text-[11px] font-semibold ${
                                                        row.free === 0
                                                            ? 'text-amber-700 dark:text-amber-300'
                                                            : 'text-gray-500 dark:text-white/55'
                                                    }`}
                                                >
                                                    {capacityLabel(row)}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </aside>

                    <section className="flex min-h-0 min-w-0 flex-1 flex-col">
                        {activeRow && activeSession ? (
                            <>
                                <div className="shrink-0 border-b border-gray-100 px-5 py-4 dark:border-white/5">
                                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                                        {activeSession.course_title || activeSession.title}
                                    </p>
                                    <p className="mt-0.5 text-sm text-gray-600 dark:text-white/70">
                                        {sessionDayLabel(activeSession.session_date, i18n.language)}{' '}
                                        {formatClock(activeSession.start_time)}–
                                        {formatClock(activeSession.end_time)}
                                    </p>
                                    <p className="mt-2 text-xs font-semibold text-amber-800 dark:text-amber-200">
                                        {capacityLabel(activeRow)}
                                    </p>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-white/65">
                                        {activeRow.candidateIds.length === 0
                                            ? t('gym.bulkEnrollment.capacity.allAlreadyHint')
                                            : activeRow.free === 0
                                              ? t('gym.bulkEnrollment.capacity.fullHint')
                                              : t('gym.bulkEnrollment.capacity.partialHint', {
                                                    count: needed,
                                                })}
                                    </p>
                                </div>

                                <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-3 sm:px-5">
                                    {activeRow.memberIds.map((userId) => {
                                        const member = membersById[idKey(userId)];
                                        const status = memberStatus(activeRow, userId);
                                        const locked =
                                            status === 'duplicate' ||
                                            status === 'alreadyWaitlist' ||
                                            activeRow.free === 0;
                                        const checked = status === 'waitlist';
                                        return (
                                            <li key={userId}>
                                                <label
                                                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${
                                                        status === 'duplicate'
                                                            ? 'bg-gray-50 dark:bg-white/5'
                                                            : status === 'alreadyWaitlist' ||
                                                                activeRow.free === 0
                                                              ? 'bg-amber-50/60 dark:bg-amber-950/20'
                                                              : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5'
                                                    }`}
                                                >
                                                    {!locked && (
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() =>
                                                                toggleWaitlist(
                                                                    activeRow.sessionId,
                                                                    userId
                                                                )
                                                            }
                                                            className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                    )}
                                                    <Users
                                                        size={14}
                                                        className="text-gray-400"
                                                        aria-hidden
                                                    />
                                                    <span className="text-sm text-gray-900 dark:text-white">
                                                        {member?.name ||
                                                            member?.username ||
                                                            `#${userId}`}
                                                    </span>
                                                    <span
                                                        className={`ml-auto text-xs font-medium ${
                                                            status === 'duplicate'
                                                                ? 'text-gray-500 dark:text-white/55'
                                                                : status === 'waitlist' ||
                                                                    status === 'alreadyWaitlist'
                                                                  ? 'text-amber-700 dark:text-amber-300'
                                                                  : 'text-gray-500 dark:text-white/55'
                                                        }`}
                                                    >
                                                        {status === 'duplicate'
                                                            ? t(
                                                                  'gym.bulkEnrollment.capacity.duplicateTag'
                                                              )
                                                            : status === 'alreadyWaitlist'
                                                              ? t(
                                                                    'gym.bulkEnrollment.capacity.alreadyWaitlistTag'
                                                                )
                                                              : status === 'waitlist'
                                                                ? t(
                                                                      'gym.bulkEnrollment.capacity.waitlistTag'
                                                                  )
                                                                : t(
                                                                      'gym.bulkEnrollment.capacity.enrollTag'
                                                                  )}
                                                    </span>
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        ) : (
                            <div className="flex flex-1 items-center justify-center px-5 text-sm text-gray-500 dark:text-white/60">
                                {t('gym.bulkEnrollment.capacity.selectSession')}
                            </div>
                        )}
                    </section>
                </div>

                <div className="shrink-0 border-t border-gray-200 px-5 py-4 dark:border-white/10">
                    {validationError && (
                        <p className="mb-3 text-sm text-red-600 dark:text-red-400">{validationError}</p>
                    )}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-gray-500 dark:text-white/55">
                            {t('gym.bulkEnrollment.capacity.readyProgress', {
                                ready: readyCount,
                                total: draft.length,
                            })}
                        </p>
                        <div className="flex flex-wrap justify-end gap-2">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:bg-gray-800 dark:text-white/85"
                            >
                                {t('gym.bulkEnrollment.capacity.cancel')}
                            </button>
                            <button
                                type="button"
                                disabled={Boolean(validationError)}
                                onClick={handleConfirm}
                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {t('gym.bulkEnrollment.capacity.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
